'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import type { RequestFormData } from './ServiceRequestWizard';
import { sendQuoteConfirmationEmail } from '@/lib/emailService';

interface QuoteResponsePanelProps {
  formData: RequestFormData;
}

type TemplateId = 'cleaning' | 'gardening' | 'handyman';

interface LineItem {
  id: string;
  description: string;
  qty: number;
  unit: string;
  rate: number;
}

const quoteTemplates: Record<TemplateId, { subject: string; intro: string; defaultItems: LineItem[] }> = {
  cleaning: {
    subject: 'Your ServiQuote Cleaning Quote — Ref SQ-2026-08114',
    intro: `Thank you for requesting a cleaning quote with ServiQuote. Based on the details you've provided, we're pleased to offer the following:`,
    defaultItems: [
      { id: 'line-cl-001', description: 'House clean — standard rooms', qty: 1, unit: 'service', rate: 220 },
      { id: 'line-cl-002', description: 'Bathroom deep clean (×2)', qty: 2, unit: 'room', rate: 45 },
      { id: 'line-cl-003', description: 'Oven & appliance clean', qty: 1, unit: 'service', rate: 65 },
      { id: 'line-cl-004', description: 'Window cleaning (interior)', qty: 1, unit: 'service', rate: 80 },
    ],
  },
  gardening: {
    subject: 'Your ServiQuote Gardening Quote — Ref SQ-2026-08114',
    intro: `Thank you for your gardening request. Here is our quote based on the property details you've provided:`,
    defaultItems: [
      { id: 'line-gd-001', description: 'Lawn mow & edge (standard block)', qty: 1, unit: 'service', rate: 85 },
      { id: 'line-gd-002', description: 'Garden bed weeding', qty: 2, unit: 'bed', rate: 35 },
      { id: 'line-gd-003', description: 'Hedge trimming & shaping', qty: 1, unit: 'service', rate: 110 },
      { id: 'line-gd-004', description: 'Green waste removal & disposal', qty: 1, unit: 'load', rate: 60 },
    ],
  },
  handyman: {
    subject: 'Your ServiQuote Handyman Quote — Ref SQ-2026-08114',
    intro: `Thanks for submitting your job request. We've reviewed the details and prepared the following quote for your consideration:`,
    defaultItems: [
      { id: 'line-hm-001', description: 'Labour — skilled tradesperson', qty: 2, unit: 'hour', rate: 95 },
      { id: 'line-hm-002', description: 'Materials & consumables (est.)', qty: 1, unit: 'allowance', rate: 75 },
      { id: 'line-hm-003', description: 'Travel & call-out fee', qty: 1, unit: 'fee', rate: 35 },
    ],
  },
};

export default function QuoteResponsePanel({ formData }: QuoteResponsePanelProps) {
  const initialTemplate: TemplateId =
    formData.serviceCategory === 'gardening' ?'gardening'
      : formData.serviceCategory === 'handyman' ?'handyman' :'cleaning';

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>(initialTemplate);
  const [lineItems, setLineItems] = useState<LineItem[]>(quoteTemplates[initialTemplate].defaultItems);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [validDays, setValidDays] = useState('7');
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const template = quoteTemplates[selectedTemplate];
  const subtotal = lineItems.reduce((sum, item) => sum + item.qty * item.rate, 0);
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  const handleTemplateChange = (t: TemplateId) => {
    setSelectedTemplate(t);
    setLineItems(quoteTemplates[t].defaultItems);
  };

  const updateItem = (id: string, field: keyof LineItem, value: string | number) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeItem = (id: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const addItem = () => {
    const newId = `line-new-${lineItems.length + 1}`;
    setLineItems((prev) => [...prev, { id: newId, description: '', qty: 1, unit: 'service', rate: 0 }]);
  };

  const handleSendQuote = async () => {
    setIsSending(true);
    try {
      await sendQuoteConfirmationEmail({
        to: formData.email || '',
        clientName: formData.firstName || 'there',
        refNumber: template.subject.match(/Ref\s+([\w-]+)/)?.[1] || 'SQ-2026',
        lineItems,
        subtotal,
        gst,
        total,
        validDays,
        additionalNotes,
        serviceCategory: selectedTemplate,
      });
    } catch (err) {
      console.error('Failed to send quote email:', err);
    }
    setIsSending(false);
    setSent(true);
  };

  return (
    <div className="space-y-5">
      {/* Admin panel header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-amber-500 rounded-full" />
            <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Admin — Quote Response</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground mt-0.5">Respond to Request SQ-2026-08114</h2>
        </div>
        <Badge variant="submitted">New Request</Badge>
      </div>

      {sent ? (
        <div className="card-surface p-6 text-center space-y-4 animate-slide-up">
          <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircleIcon" size={28} variant="outline" className="text-primary" />
          </div>
          <div>
            <p className="font-semibold text-foreground">Quote sent successfully</p>
            <p className="text-sm text-muted-foreground mt-1">
              Quote emailed to <span className="font-medium">{formData.email || 'client'}</span>. Awaiting acceptance.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button onClick={() => setSent(false)} className="px-4 py-2 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted transition-colors">
              Edit & Resend
            </button>
            <a href="/client-job-portal" className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-colors text-center">
              View in Client Portal
            </a>
          </div>
        </div>
      ) : (
        <>
          {/* Template selector */}
          <div className="card-surface p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Email Template</p>
            <div className="grid grid-cols-3 gap-2">
              {(['cleaning', 'gardening', 'handyman'] as TemplateId[]).map((t) => (
                <button
                  key={`tmpl-${t}`}
                  type="button"
                  onClick={() => handleTemplateChange(t)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all duration-150 capitalize ${
                    selectedTemplate === t
                      ? 'bg-secondary border-primary text-primary' :'bg-muted/30 border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  {t === 'gardening' ? 'Gardening' : t === 'handyman' ? 'Handyman' : 'Cleaning'}
                </button>
              ))}
            </div>
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs font-medium text-foreground mb-0.5">Subject: {template.subject}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{template.intro}</p>
            </div>
          </div>

          {/* Line items */}
          <div className="card-surface p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Quote Line Items</p>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left pb-2 text-muted-foreground font-medium pr-2">Description</th>
                    <th className="text-center pb-2 text-muted-foreground font-medium w-12">Qty</th>
                    <th className="text-left pb-2 text-muted-foreground font-medium w-20">Unit</th>
                    <th className="text-right pb-2 text-muted-foreground font-medium w-20">Rate (AUD)</th>
                    <th className="text-right pb-2 text-muted-foreground font-medium w-20">Total</th>
                    <th className="w-6" />
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item) => (
                    <tr key={item.id} className="border-b border-border/50 quote-line-row">
                      <td className="py-2 pr-2">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-transparent rounded focus:border-input focus:outline-none focus:ring-1 focus:ring-ring bg-transparent hover:bg-card"
                        />
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="number"
                          min={1}
                          value={item.qty}
                          onChange={(e) => updateItem(item.id, 'qty', Number(e.target.value))}
                          className="w-12 px-1 py-1 text-xs text-center border border-transparent rounded focus:border-input focus:outline-none focus:ring-1 focus:ring-ring bg-transparent hover:bg-card font-tabular"
                        />
                      </td>
                      <td className="py-2">
                        <input
                          type="text"
                          value={item.unit}
                          onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-transparent rounded focus:border-input focus:outline-none focus:ring-1 focus:ring-ring bg-transparent hover:bg-card"
                        />
                      </td>
                      <td className="py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          value={item.rate}
                          onChange={(e) => updateItem(item.id, 'rate', Number(e.target.value))}
                          className="w-20 px-2 py-1 text-xs text-right border border-transparent rounded focus:border-input focus:outline-none focus:ring-1 focus:ring-ring bg-transparent hover:bg-card font-tabular"
                        />
                      </td>
                      <td className="py-2 text-right font-tabular font-medium text-foreground">
                        ${(item.qty * item.rate).toFixed(2)}
                      </td>
                      <td className="py-2 pl-1">
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="p-0.5 text-muted-foreground hover:text-red-500 transition-colors"
                          aria-label="Remove line item"
                        >
                          <Icon name="XMarkIcon" size={12} variant="outline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline"
            >
              <Icon name="PlusIcon" size={13} variant="outline" />
              Add line item
            </button>

            {/* Totals */}
            <div className="border-t border-border pt-3 space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Subtotal</span>
                <span className="font-tabular">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>GST (10%)</span>
                <span className="font-tabular">${gst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-foreground border-t border-border pt-1.5">
                <span>Total (inc. GST)</span>
                <span className="font-tabular text-primary">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Additional notes & validity */}
          <div className="card-surface p-4 space-y-4">
            <div>
              <label htmlFor="additionalNotes" className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                Additional Notes to Client
              </label>
              <textarea
                id="additionalNotes"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                rows={3}
                placeholder="e.g. Price may vary slightly depending on actual site conditions. Please ensure all areas are accessible on the day. Includes all cleaning products and equipment."
                className="w-full px-3 py-2 text-xs border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
            <div>
              <label htmlFor="validDays" className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                Quote Valid For
              </label>
              <select
                id="validDays"
                value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
                className="px-3 py-2 text-xs border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="3">3 days</option>
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-4 py-2.5 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground transition-all duration-150"
            >
              <Icon name="EyeIcon" size={15} variant="outline" />
              {showPreview ? 'Hide Preview' : 'Preview Email'}
            </button>
            <button
              type="button"
              onClick={handleSendQuote}
              disabled={isSending || lineItems.length === 0}
              className="flex items-center justify-center gap-2 min-w-[160px] px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <Icon name="ArrowPathIcon" size={15} variant="outline" className="animate-spin" />
                  Sending Quote...
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={15} variant="outline" />
                  Send Quote via Email
                </>
              )}
            </button>
          </div>

          {/* Email preview */}
          {showPreview && (
            <div className="card-surface p-5 border-2 border-dashed border-border space-y-4 animate-slide-up">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Email Preview</p>
                <span className="text-xs text-muted-foreground">To: {formData.email || 'client@email.com'}</span>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-foreground">Subject: {template.subject}</p>
                <hr className="border-border" />
                <p className="text-xs text-muted-foreground">
                  Hi {formData.firstName || 'there'},
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">{template.intro}</p>
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted px-3 py-2 grid grid-cols-4 text-xs font-semibold text-muted-foreground">
                    <span className="col-span-2">Description</span>
                    <span className="text-right">Qty</span>
                    <span className="text-right">Total</span>
                  </div>
                  {lineItems.map((item) => (
                    <div key={`prev-${item.id}`} className="px-3 py-1.5 grid grid-cols-4 text-xs border-t border-border">
                      <span className="col-span-2 text-foreground">{item.description || '—'}</span>
                      <span className="text-right text-muted-foreground font-tabular">{item.qty}</span>
                      <span className="text-right text-foreground font-tabular">${(item.qty * item.rate).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="px-3 py-2 bg-secondary border-t border-border flex justify-between text-xs font-bold text-primary">
                    <span>Total (inc. GST)</span>
                    <span className="font-tabular">${total.toFixed(2)}</span>
                  </div>
                </div>
                {additionalNotes && (
                  <p className="text-xs text-muted-foreground italic leading-relaxed">{additionalNotes}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  This quote is valid for {validDays} days. To accept this quote and book your service, click the button below.
                </p>
                <div className="flex justify-center">
                  <span className="px-6 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-lg">
                    Accept Quote & Book Service
                  </span>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  ServiQuote · hello@serviquote.com.au · 1300 SERVI Q
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}